/**
 * useScenarioLoader
 *
 * Handles loading existing scenarios from the backend and
 * initializing state for new scenarios (including prefill from DER analysis).
 *
 * Extracted from ScenarioEditor to reduce component complexity.
 * Owns loading-related state and shared refs used by save logic.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { debug } from '../utils/debug';
import { reoptToDailySeries } from '../reoptTransform';
import { hydrateElectricTariff, hydrateGasTariff } from '../utils/tariffHydration';
import { convertGasTariffForSolver } from '../utils/gasTariffConverter';
import { API_BASE_URL } from '../services/api';

export function useScenarioLoader({
    projectId,
    scenarioId,
    getHeaders,
    scenarioForm,
    loadProfile,
    optimization,
    setVortxSettings,
    setProjectContext,
    projectName,
    setLastRunTimestamp,
    setLastSavedSnapshot,
    setLoadedBudgetState,
    onLoaded, // ({ updatedAt }) => void — called after successful load
}) {
    const [isLoading, setIsLoading] = useState(true);
    const [exists, setExists] = useState(true);

    // Shared refs (exposed for save logic and other effects)
    const resultsSavedRef = useRef(false);
    const lastSavedRunUuidRef = useRef(null);
    const resultsSaveInProgressRef = useRef(false);
    const savedFinancialSummaryRef = useRef(null);
    const cleanStateSnapshotRef = useRef(null);
    const loadedStateRef = useRef(null);
    const financialSummarySavedForRunRef = useRef(null);

    // Keep latest dependencies in a ref to avoid stale closures
    // (loadScenario is useCallback with [] deps for stability)
    const depsRef = useRef({});
    depsRef.current = {
        projectId,
        scenarioId,
        getHeaders,
        scenarioForm,
        loadProfile,
        optimization,
        setVortxSettings,
        setProjectContext,
        projectName,
        setLastRunTimestamp,
        setLastSavedSnapshot,
        setLoadedBudgetState,
        onLoaded,
    };

    /**
     * Load an existing scenario from the backend.
     * Restores form state, load profile, tariffs, results, and budget.
     */
    const loadScenario = useCallback(async () => {
        const deps = depsRef.current;
        setIsLoading(true);
        try {
            const headers = await deps.getHeaders();
            const res = await fetch(
                `${API_BASE_URL}/api/site_dna/v1/projects/${deps.projectId}/scenarios/${deps.scenarioId}`,
                { headers }
            );
            if (res.ok) {
                const data = await res.json();
                debug.log("Loaded scenario data:", data);
                debug.log("📥 Loaded scenario name info:", {
                    topLevelName: data.name,
                    uiStateFormSiteName: data.ui_state?.form?.site?.scenarioName,
                    match: data.name === data.ui_state?.form?.site?.scenarioName
                });
                debug.log("📥 Loaded tariff info:", {
                    tariff_label: data.tariff_label,
                    tariff_data_id: data.tariff_data?.id,
                    tariff_data_name: data.tariff_data?.name,
                    has_tariff_data: !!data.tariff_data
                });

                // Update retro context with scenario for breadcrumb
                deps.setProjectContext(
                    { id: deps.projectId, name: deps.projectName },
                    { id: deps.scenarioId, name: data.name }
                );

                // Extract loadYear from saved state (needed for proper timestamp calculation)
                const savedLoadYear = data.ui_state?.loadProfile?.loadYear || String(new Date().getFullYear());

                // Restore UI state (form and load profile)
                if (data.ui_state) {
                    deps.scenarioForm.loadFormState(data.ui_state.form);
                    deps.loadProfile.loadLoadProfile(data.ui_state.loadProfile);
                    // Note: Tab is now handled by URL hash, not ui_state

                    // IMPORTANT: Override scenarioName from authoritative top-level name field
                    // The ui_state.form.site.scenarioName can get out of sync when renamed from project view
                    // data.name is the source of truth (stored in Firestore metadata)
                    if (data.name) {
                        deps.scenarioForm.setScenarioName(data.name);
                    }

                    // CRITICAL: Capture snapshot directly from loaded data, not from React state
                    // This avoids timing issues with React re-renders
                    // IMPORTANT: Fields must match exactly with isDirty useMemo
                    // NOTE: Form data is saved with nested structure (site, solar, battery, generator, tariff)
                    const form = data.ui_state.form || {};
                    const loadedSnapshot = {
                        // Use top-level name as source of truth (may differ from ui_state when renamed from project view)
                        scenarioName: data.name || form.site?.scenarioName || '',
                        siteName: form.site?.siteName || '',
                        zipCode: form.site?.zipCode || '',
                        street: form.site?.street || '',
                        city: form.site?.city || '',
                        state: form.site?.state || '',
                        usePv: form.solar?.usePv || false,
                        pvMaxKw: form.solar?.pvMaxKw || '',
                        pvCost: form.solar?.pvCost || '',
                        useStorage: form.battery?.useStorage || false,
                        storageMaxKw: form.battery?.storageMaxKw || '',
                        storageMaxKwh: form.battery?.storageMaxKwh || '',
                        useGenerator: form.generator?.useGenerator || false,
                        generatorMaxKw: form.generator?.generatorMaxKw || '',
                        blendedRate: form.tariff?.blendedRate || '',
                        offGrid: form.site?.offGrid || false,
                        gasTariffId: data.gas_tariff_id || null,
                        gasTariffData: data.gas_tariff_data || null,
                    };
                    debug.log('[SNAPSHOT] Captured clean state directly from loaded data:', loadedSnapshot);
                    cleanStateSnapshotRef.current = loadedSnapshot;

                    // Initialize last saved snapshot for periodic autosave
                    deps.setLastSavedSnapshot(JSON.stringify(loadedSnapshot));
                    debug.log('[AUTOSAVE] Initialized last saved snapshot from loaded data');
                }

                // Hydrate electric tariff (extracted to utility)
                await hydrateElectricTariff({
                    data,
                    headers,
                    setSelectedTariffData: deps.scenarioForm.setSelectedTariffData,
                    setCustomTariffData: deps.scenarioForm.setCustomTariffData,
                    setVortxSettings: deps.setVortxSettings,
                });

                // Hydrate gas tariff (extracted to utility)
                await hydrateGasTariff({
                    data,
                    headers,
                    setGasTariffData: deps.scenarioForm.setGasTariffData,
                    setGasTariffId: deps.scenarioForm.setGasTariffId,
                    setGeneratorFuelCostPerMmbtu: deps.scenarioForm.setGeneratorFuelCostPerMmbtu,
                    convertGasTariffForSolver,
                });

                // Restore saved optimization results if available
                if (data.last_run_uuid) {
                    debug.log("Found last_run_uuid:", data.last_run_uuid);

                    // Create a transform function with the saved loadYear
                    const transformWithSavedYear = (results, dayIdx = 0, stepsPerHour = 1) => {
                        return reoptToDailySeries(results, dayIdx, stepsPerHour, savedLoadYear);
                    };

                    // STEP 1: Load saved results FIRST (for immediate display while checking status)
                    if (data.last_run_results) {
                        debug.log("Loading saved results for display");
                        deps.optimization.loadSavedResults({
                            results: data.last_run_results,
                            runUuid: data.last_run_uuid,
                            inputsHash: data.inputs_hash,
                        }, transformWithSavedYear);

                        // Restore timestamp if available
                        if (data.last_run_timestamp) {
                            deps.setLastRunTimestamp(new Date(data.last_run_timestamp));
                        }

                        // Mark these results as already saved (since we just loaded them from backend)
                        resultsSavedRef.current = true;
                        lastSavedRunUuidRef.current = data.last_run_uuid;
                        debug.log('✅ [OPTIMIZATION] Loaded results marked as saved (UUID:', data.last_run_uuid, ')');
                    }

                    // STEP 2: Check if there's a run in progress (may be the same or newer run)
                    try {
                        const statusHeaders = await deps.getHeaders();
                        const statusRes = await fetch(`${API_BASE_URL}/api/site_dna/status/${data.last_run_uuid}`, { headers: statusHeaders });

                        // Handle expected job-not-found cases (solver redeployed, old scenario, etc.)
                        if (!statusRes.ok) {
                            if (statusRes.status === 404) {
                                console.log(`ℹ️ No simulation running - using saved results from previous optimization`);
                            } else if (statusRes.status === 502 || statusRes.status === 500) {
                                console.log(`ℹ️ Solver temporarily unavailable - using saved results`);
                            } else {
                                console.warn(`Status check returned ${statusRes.status} - falling back to saved results`);
                            }
                            // Fall through to use saved results from STEP 1
                        } else {
                            const statusData = await statusRes.json();
                            const runStatus = statusData.status || 'unknown';
                            debug.log("Run status check:", runStatus);

                            // Case-insensitive check for in-progress statuses
                            const runStatusLower = runStatus.toLowerCase();
                            const inProgressStatuses = ['pending', 'processing', 'running', 'waiting'];
                            const completedStatuses = ['optimal', 'completed', 'optimal obtained'];

                            if (inProgressStatuses.includes(runStatusLower)) {
                                // Run is in progress - start polling
                                // Old results are already displayed from STEP 1
                                debug.log("🔄 Run in progress, starting polling (old results shown)");

                                // CRITICAL: Set status to Running AFTER loadSavedResults,
                                // because loadSavedResults sets status to "Loaded from save"
                                deps.optimization.setStatus('Running');
                                deps.optimization.setRunUuid(data.last_run_uuid);
                                deps.optimization.setLastRunUuid(data.last_run_uuid);

                                deps.optimization.startPolling(data.last_run_uuid, {
                                    transformResults: transformWithSavedYear,
                                    onComplete: async (resultsData) => {
                                        debug.log("✅ Optimization completed, updating display");
                                        // Use actual completion timestamp from results if available
                                        const runTs = resultsData.completed_at || resultsData.timestamp;
                                        depsRef.current.setLastRunTimestamp(runTs ? new Date(runTs) : new Date());

                                        // Reset results saved flag since we have new results to save
                                        resultsSavedRef.current = false;
                                        debug.log('🔄 [OPTIMIZATION] Resetting results saved flag for new optimization results (from polling)');
                                    },
                                    onError: (error) => {
                                        debug.error("Optimization failed:", error);
                                    }
                                });
                            } else if (completedStatuses.includes(runStatusLower)) {
                                // Run completed - update status to show success
                                deps.optimization.setStatus('Success');

                                // Always try to load results from status response for freshest data
                                // (saved results might be from a different/older run)
                                if (statusData.outputs || statusData.results) {
                                    debug.log("Loading completed results from status response");
                                    const resultsFromStatus = statusData.outputs ? statusData : { outputs: statusData.results };
                                    deps.optimization.loadSavedResults({
                                        results: resultsFromStatus,
                                        runUuid: data.last_run_uuid,
                                    }, transformWithSavedYear);
                                    // Re-set status since loadSavedResults overwrites it
                                    deps.optimization.setStatus('Success');

                                    // Set timestamp - use saved timestamp if available, otherwise use now
                                    if (data.last_run_timestamp) {
                                        deps.setLastRunTimestamp(new Date(data.last_run_timestamp));
                                    } else {
                                        deps.setLastRunTimestamp(new Date()); // Fallback to now
                                    }
                                } else if (data.last_run_timestamp) {
                                    // Results were loaded from STEP 1, just set timestamp
                                    deps.setLastRunTimestamp(new Date(data.last_run_timestamp));
                                }
                            }
                            // else: unknown status, just use saved results from STEP 1
                        }
                    } catch (statusError) {
                        debug.log("Could not check optimization status (network error) - using saved results:", statusError.message);
                        // Fall back to saved results (already loaded in STEP 1)
                    }
                } else if (data.optimization_runs?.length > 0) {
                    // Check for IN-PROGRESS runs first - resume polling if found
                    const inProgressRun = data.optimization_runs.find(run =>
                        ['pending', 'processing', 'running', 'Waiting'].includes(run.status)
                    );

                    if (inProgressRun) {
                        debug.log("🔄 Found in-progress run, resuming polling:", inProgressRun.nrel_run_uuid);
                        deps.optimization.setStatus(inProgressRun.status);
                        deps.optimization.setRunUuid(inProgressRun.nrel_run_uuid);
                        deps.optimization.setLastRunUuid(inProgressRun.nrel_run_uuid);

                        // Create transform function for when results arrive
                        const transformWithSavedYear = (results, dayIdx = 0, stepsPerHour = 1) => {
                            return reoptToDailySeries(results, dayIdx, stepsPerHour, savedLoadYear);
                        };

                        // Resume polling - the backend is still processing
                        deps.optimization.startPolling(inProgressRun.nrel_run_uuid, {
                            transformResults: transformWithSavedYear,
                            onComplete: async (resultsData) => {
                                debug.log("✅ Resumed optimization completed!");
                            },
                            onError: (error) => {
                                debug.error("Resumed optimization failed:", error);
                            }
                        });

                        // Skip looking for completed runs since we're resuming an active one
                    } else {
                        // Fallback: Load results from most recent completed run
                        // This handles scenarios created before last_run_results persistence
                        const latestRun = data.optimization_runs.find(run =>
                            run.status === 'optimal' || run.status === 'completed'
                        );

                        if (latestRun) {
                            debug.log("Found recent run, checking for results:", latestRun.nrel_run_uuid);

                            let resultsToLoad = latestRun.results;

                            // If results are not in the list (due to backend optimization), fetch them
                            if (!resultsToLoad) {
                                debug.log("Results not in list payload, fetching explicitly for run:", latestRun.id);
                                try {
                                    const runRes = await fetch(
                                        `${API_BASE_URL}/api/site_dna/v1/projects/${deps.projectId}/scenarios/${deps.scenarioId}/runs/${latestRun.id}/results`,
                                        { headers }
                                    );
                                    if (runRes.ok) {
                                        const runData = await runRes.json();
                                        resultsToLoad = runData.results || runData.outputs;
                                    }
                                } catch (err) {
                                    debug.error("Failed to fetch legacy run results:", err);
                                }
                            }

                            if (resultsToLoad) {
                                const transformWithSavedYear = (results, dayIdx = 0, stepsPerHour = 1) => {
                                    return reoptToDailySeries(results, dayIdx, stepsPerHour, savedLoadYear);
                                };

                                deps.optimization.loadSavedResults({
                                    results: resultsToLoad,
                                    runUuid: latestRun.nrel_run_uuid,
                                }, transformWithSavedYear);
                                debug.log("Restored results from optimization_runs fallback");
                            } else {
                                debug.log("Could not retrieve results for latest run");
                            }
                        } else {
                            debug.log("No completed runs found, starting fresh");
                        }
                    }
                } else {
                    debug.log("No saved results found, starting fresh");
                }

                // Extract budget state for restoration
                if (data.ui_state?.budget) {
                    deps.setLoadedBudgetState(data.ui_state.budget);
                    debug.log('💰 [BUDGET] Loaded budget state from backend');
                }

                // Preserve saved financialSummary for comparison modal
                if (data.ui_state?.financialSummary) {
                    savedFinancialSummaryRef.current = data.ui_state.financialSummary;
                    debug.log('📊 [FINANCIAL] Loaded financialSummary from backend:', data.ui_state.financialSummary);
                }

                // Extract commercial assumptions for restoration
                if (data.ui_state?.commercialAssumptions) {
                    deps.scenarioForm.setCommercialAssumptions(data.ui_state.commercialAssumptions);
                    debug.log('📊 [COMMERCIAL] Loaded commercial assumptions from backend');
                }

                // Load equipment selections (will be fetched when BudgetTab mounts)
                if (data.ui_state?.selectedEquipment) {
                    const equipmentIds = data.ui_state.selectedEquipment;
                    debug.log('🔧 [EQUIPMENT] Loaded equipment selection IDs from backend:', equipmentIds);
                    window.sessionStorage.setItem('pendingEquipmentLoad', JSON.stringify(equipmentIds));
                }

                // Notify caller that load is complete (for optimistic sync)
                depsRef.current.onLoaded?.({ updatedAt: data.updated_at });

                // Delay enabling dirty tracking to allow React to fully hydrate the form
                setTimeout(() => {
                    loadedStateRef.current = true;
                    debug.log('[SNAPSHOT] ✅ Dirty tracking enabled after form hydration delay');
                }, 1500);

                debug.log('✅ Scenario loaded from server, version:', data.updated_at);

            } else {
                // Scenario not found (404) - mark as non-existent to prevent auto-save attempts
                debug.error(`Scenario ${deps.scenarioId} not found (status ${res.status})`);
                setExists(false);
                deps.scenarioForm.setSnackbar({
                    open: true,
                    message: `Scenario not found. It may have been deleted or you don't have access.`,
                    severity: "error",
                });
            }
        } catch (e) {
            debug.error("Failed to load scenario", e);
            setExists(false);
        } finally {
            setIsLoading(false);
        }
    }, []); // stable — reads all deps from depsRef

    /**
     * Initialize state for a new scenario.
     * Handles prefill from DER variant analysis (via sessionStorage or URL param)
     * or resets to clean defaults.
     */
    const initNewScenario = useCallback(() => {
        const deps = depsRef.current;

        // Check for prefill data from DER variant analysis
        // Supports both sessionStorage (prefillKey) and legacy URL param (prefill)
        const searchParams = new URLSearchParams(window.location.search);
        const prefillKey = searchParams.get('prefillKey');
        const prefillData = searchParams.get('prefill');

        // Try sessionStorage first (for large scenarios), fall back to URL param
        let decoded = null;
        if (prefillKey) {
            try {
                const stored = sessionStorage.getItem(prefillKey);
                if (stored) {
                    decoded = JSON.parse(stored);
                    sessionStorage.removeItem(prefillKey);
                    debug.log("Applying prefill data from sessionStorage:", decoded);
                }
            } catch (e) {
                debug.error("Failed to parse prefill from sessionStorage:", e);
            }
        } else if (prefillData) {
            try {
                decoded = JSON.parse(atob(prefillData));
                debug.log("Applying prefill data from URL param:", decoded);
            } catch (e) {
                debug.error("Failed to parse prefill from URL:", e);
            }
        }

        if (decoded) {
            try {
                // Apply prefilled scenario data
                if (decoded.name) {
                    deps.scenarioForm.setScenarioName(decoded.name);
                }

                // Apply Site data
                if (decoded.Site) {
                    deps.scenarioForm.setSiteData(prev => ({ ...prev, ...decoded.Site }));
                }

                // Apply PV configuration
                if (decoded.PV) {
                    deps.scenarioForm.setSolarData(prev => ({
                        ...prev,
                        newSolarKw: decoded.PV.max_kw || decoded.PV.min_kw || 0
                    }));
                }

                // Apply Battery configuration
                if (decoded.ElectricStorage) {
                    deps.scenarioForm.setBatteryData(prev => ({
                        ...prev,
                        newBatteryKwh: decoded.ElectricStorage.max_kwh || 0,
                        newBatteryKw: decoded.ElectricStorage.max_kw || 0
                    }));
                }

                // Apply Generator configuration
                if (decoded.Generator) {
                    deps.scenarioForm.setGeneratorData(prev => ({
                        ...prev,
                        generatorKw: decoded.Generator.max_kw || 0
                    }));
                }

                // Store analysis results reference if available
                if (decoded._analysis_results) {
                    debug.log("Reference: NPV=$" + decoded._analysis_results.npv +
                        ", Payback=" + decoded._analysis_results.payback + " yrs");
                }

                // Clean up URL (remove prefill param but preserve hash and other query params)
                const url = new URL(window.location.href);
                url.searchParams.delete('prefill');
                window.history.replaceState({}, '', url.toString());

            } catch (e) {
                debug.error("Failed to apply prefill data:", e);
            }
        } else {
            // Comprehensive Reset using Hook Methods
            debug.log("Resetting state for new scenario");

            // 1. Reset Scenario Form (Site, Tariffs, Technologies)
            deps.scenarioForm.resetAll();

            // 2. Reset Load Profile (Loads, Source, Model)
            deps.loadProfile.resetLoadProfile();

            // 3. Reset Optimization (Results, Status, UUIDs)
            deps.optimization.resetOptimization();

            // 4. Initialize dirty tracking baseline for new scenarios
            // Without this, isDirty is always false (lastSavedSnapshot is null),
            // which prevents auto-save from ever firing — renames, AI data, etc.
            // are lost unless the user manually clicks Save.
            // Values must match exactly what resetAll() sets above.
            deps.setLastSavedSnapshot(JSON.stringify({
                scenarioName: "",
                siteName: "",
                zipCode: "",
                street: "",
                city: "",
                state: "",
                usePv: false,
                pvMaxKw: "",
                pvCost: String(deps.scenarioForm.DEFAULT_PV_COST_PER_KW),
                useStorage: false,
                storageMaxKw: "",
                storageMaxKwh: "",
                useGenerator: false,
                generatorMaxKw: "",
                offGrid: false,
                gasTariffId: null,
                gasTariffData: null,
            }));
        }
    }, []); // stable — reads all deps from depsRef

    // Initialize on mount — load existing scenario or setup new
    useEffect(() => {
        if (projectId && scenarioId && scenarioId !== "new") {
            loadScenario();
        } else {
            // New scenario - reset state
            setIsLoading(false);
            initNewScenario();
        }
    }, [projectId, scenarioId, loadScenario, initNewScenario]);

    return {
        isLoading,
        exists,
        loadScenario,
        // Shared refs (needed by save logic and other effects)
        resultsSavedRef,
        lastSavedRunUuidRef,
        resultsSaveInProgressRef,
        savedFinancialSummaryRef,
        cleanStateSnapshotRef,
        loadedStateRef,
        financialSummarySavedForRunRef,
    };
}
